// Libs
import classNames from 'classnames/bind';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Box,
  IconButton,
  Avatar,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { Loading } from '~/components/common';
// Others
import { IProductAvailability, JobProductAvailability } from '~/utils/interface/jobs';
import { getDaysInMonth, getMonthRange } from '~/utils/helper/common';
import { DateFormatEnum, TypeDetailInventoryEnum } from '~/utils/enum/common';
import { IParamDetailInventory } from '~/utils/interface/inventory';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailInventory } from '~/thunks/inventory/inventoryThunk';
import { EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './ProductAvailabilityTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const ProductAvailabilityTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const { id: inventoryId } = useParams();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [listJobAvailability, setListJobAvailability] = useState<JobProductAvailability[]>([]);
  const [currentYear, setCurrentYear] = useState(moment().year());
  const [currentMonth, setCurrentMonth] = useState(moment().month());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [queryParams, setQueryParams] = useState<IParamDetailInventory>({
    inventoryId: inventoryId || EMPTY_STRING,
    type: TypeDetailInventoryEnum.PRODUCT_AVAILABILITY,
    query: {
      startDate: getMonthRange(currentYear, currentMonth).startDate,
      endDate: getMonthRange(currentYear, currentMonth).endDate,
    },
  });
  //#endregion Declare State

  //#region Declare Memo
  const daysInMonth = useMemo(() => getDaysInMonth(currentYear, currentMonth + 1), [currentYear, currentMonth]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!inventoryId) return;

    handleGetDetailInventory(queryParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailInventory = (params: IParamDetailInventory) => {
    setIsLoading(true);

    dispatch(getDetailInventory(params))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        const convertData = handleConvertListProductAvailability(data as IProductAvailability[]);

        setListJobAvailability(convertData);
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleConvertListProductAvailability = (data: IProductAvailability[]): JobProductAvailability[] => {
    return data.map((item) => {
      const startDate = moment.min(item.jobEvents.map((e) => moment(e.day))).format('YYYY-MM-DD');

      const endDate = moment.max(item.jobEvents.map((e) => moment(e.day))).format('YYYY-MM-DD');

      const firstEvent = item.jobEvents[0];
      const totalItem = Number(firstEvent.quantity) + Number(firstEvent.subRentalQuantity);

      return {
        id: item.id,
        name: item.title,
        startDate,
        endDate,
        totalItem,
      };
    });
  };

  const handlePrevMonth = () => {
    const prev = moment({ year: currentYear, month: currentMonth }).subtract(1, 'month');

    setCurrentYear(prev.year());
    setCurrentMonth(prev.month());

    const { startDate, endDate } = getMonthRange(prev.year(), prev.month());

    setQueryParams((prevParams) => ({
      ...prevParams,
      query: { startDate, endDate },
    }));
  };

  const handleNextMonth = () => {
    const next = moment({ year: currentYear, month: currentMonth }).add(1, 'month');

    setCurrentYear(next.year());
    setCurrentMonth(next.month());

    const { startDate, endDate } = getMonthRange(next.year(), next.month());

    setQueryParams((prevParams) => ({
      ...prevParams,
      query: { startDate, endDate },
    }));
  };
  //#endregion Handle Function

  return (
    <Stack padding={4} spacing={'10px'} className={cx('container')}>
      <Stack direction='row' alignItems='center' justifyContent='start' spacing={2}>
        <IconButton className={cx('iconButton')} onClick={handlePrevMonth}>
          <Avatar
            className={cx('icon')}
            src={icons.commonIconPreviousActive}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
        <Typography className={cx('monthTitle')}>
          {moment({ year: currentYear, month: currentMonth }).format(DateFormatEnum.MMMM_YYYY)}
        </Typography>
        <IconButton className={cx('iconButton')} onClick={handleNextMonth}>
          <Avatar
            className={cx('icon')}
            src={icons.commonIconNextActive}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </Stack>

      <TableContainer component={Paper} className={cx('tableWrapper')}>
        <Table stickyHeader className={cx('table')}>
          <TableHead>
            <TableRow>
              <TableCell className={cx('tableHeaderCell', 'tableName')}>
                {t('job_product_availability_title_job_name')}
              </TableCell>
              {Array.from({ length: daysInMonth }, (_, index) => (
                <TableCell className={cx('tableHeaderCell')} key={index} align='center'>
                  {index + 1}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className={cx('tableBody')}>
            {listJobAvailability.length > 0 ? (
              listJobAvailability.map((job) => {
                const start = moment(job.startDate).date();
                const end = moment(job.endDate).date();

                return (
                  <TableRow key={job.id}>
                    <TableCell className={cx('tableBodyCell', 'tableName')}>{job.name}</TableCell>
                    {Array.from({ length: daysInMonth }, (_, dayIndex) => {
                      const day = dayIndex + 1;
                      const isInRange = day >= start && day <= end;

                      return (
                        <TableCell className={cx('tableBodyCell')} key={`${job.id}-${day}`} align='center'>
                          {isInRange ? <Box className={cx('textValue')}>{job.totalItem}</Box> : null}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <Box className={cx('noData')}>{t('common_no_data_available', { ns: 'common/base' })}</Box>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ProductAvailabilityTab;
